/**
 * 时间格式化工具函数
 * 使用 dayjs 库统一处理时间格式化
 */
import dayjs from 'dayjs'
import 'dayjs/locale/zh-cn'

// 设置dayjs为中文
dayjs.locale('zh-cn')

/**
 * 格式化日期时间
 * @param {string|Date|number} dateTime - 日期时间
 * @param {string} format - 格式化模板，默认 'YYYY-MM-DD HH:mm:ss'
 * @returns {string} 格式化后的日期时间字符串
 */
export function formatDateTime(dateTime, format = 'YYYY-MM-DD HH:mm:ss') {
  if (!dateTime) return '-'
  
  try {
    // 如果是字符串格式的日期时间（MySQL返回的格式），直接解析
    const date = dayjs(dateTime)
    
    // 检查日期是否有效
    if (!date.isValid()) {
      console.warn('无效的日期时间:', dateTime)
      return String(dateTime)
    }
    
    return date.format(format)
  } catch (error) {
    console.error('格式化日期时间失败:', error, dateTime)
    return String(dateTime)
  }
}

/**
 * 格式化日期（不包含时间）
 * @param {string|Date|number} date - 日期
 * @param {string} format - 格式化模板，默认 'YYYY-MM-DD'
 * @returns {string} 格式化后的日期字符串
 */
export function formatDate(date, format = 'YYYY-MM-DD') {
  if (!date) return '-'
  
  try {
    const d = dayjs(date)
    
    if (!d.isValid()) {
      console.warn('无效的日期:', date)
      return String(date)
    }
    
    return d.format(format)
  } catch (error) {
    console.error('格式化日期失败:', error, date)
    return String(date)
  }
}

/**
 * 格式化时间（不包含日期）
 * @param {string|Date|number} time - 时间
 * @param {string} format - 格式化模板，默认 'HH:mm:ss'
 * @returns {string} 格式化后的时间字符串
 */
export function formatTime(time, format = 'HH:mm:ss') {
  if (!time) return '-'
  
  try {
    const t = dayjs(time)
    
    if (!t.isValid()) {
      console.warn('无效的时间:', time)
      return String(time)
    }
    
    return t.format(format)
  } catch (error) {
    console.error('格式化时间失败:', error, time)
    return String(time)
  }
}

/**
 * 相对时间（例如：3分钟前、2小时前）
 * @param {string|Date|number} dateTime - 日期时间
 * @returns {string} 相对时间字符串
 */
export function formatRelativeTime(dateTime) {
  if (!dateTime) return '-'
  
  try {
    const date = dayjs(dateTime)
    
    if (!date.isValid()) {
      return String(dateTime)
    }
    
    return date.fromNow()
  } catch (error) {
    console.error('格式化相对时间失败:', error, dateTime)
    return String(dateTime)
  }
}

/**
 * 格式化日期时间范围
 * @param {string|Date|number} startTime - 开始时间
 * @param {string|Date|number} endTime - 结束时间
 * @returns {string} 格式化后的时间范围字符串
 */
export function formatDateTimeRange(startTime, endTime) {
  if (!startTime || !endTime) return '-'
  
  try {
    const start = dayjs(startTime)
    const end = dayjs(endTime)
    
    if (!start.isValid() || !end.isValid()) {
      return `${startTime} ~ ${endTime}`
    }
    
    // 如果是同一天，只显示一次日期
    if (start.format('YYYY-MM-DD') === end.format('YYYY-MM-DD')) {
      return `${start.format('YYYY-MM-DD HH:mm')} ~ ${end.format('HH:mm')}`
    }
    
    return `${start.format('YYYY-MM-DD HH:mm')} ~ ${end.format('YYYY-MM-DD HH:mm')}`
  } catch (error) {
    console.error('格式化时间范围失败:', error)
    return `${startTime} ~ ${endTime}`
  }
}

export default {
  formatDateTime,
  formatDate,
  formatTime,
  formatRelativeTime,
  formatDateTimeRange
}
